import { useState, useRef, useEffect } from "react";
import { PlayArrow, Pause, VolumeUp, VolumeOff, GetApp } from "@material-ui/icons";
import { IconButton, Slider, useMediaQuery, Menu, MenuItem, Popover } from "@material-ui/core";

const COLOR_BG = "#111928";
const COLOR_SURFACE = "#374151";
const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

const CustomAudio = ({ src, filename, hideDownload = false, hideSpeed = false, hideVolume = false }) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [speed, setSpeed] = useState(1);
    const [speedAnchor, setSpeedAnchor] = useState(null);
    const [volumeAnchor, setVolumeAnchor] = useState(null);

    const isMobileScreen = useMediaQuery("(max-width: 576px)");

    const togglePlay = () => {
        if (isPlaying) audioRef.current.pause();
        else audioRef.current.play();
        setIsPlaying(!isPlaying);
    };

    const handleSeek = (_, newValue) => {
        audioRef.current.currentTime = newValue;
        setCurrentTime(newValue);
    };

    const handleVolume = (_, newValue) => {
        audioRef.current.volume = newValue;
        setVolume(newValue);
        setIsMuted(newValue === 0);
    };

    const handleSpeed = (s) => {
        audioRef.current.playbackRate = s;
        setSpeed(s);
        setSpeedAnchor(null);
    };

    const _toggleMute = () => {
        const next = !isMuted;
        audioRef.current.muted = next;
        setIsMuted(next);
    };

    const handleDownload = () => {
        const a = document.createElement("a");
        a.href = src;
        a.target = "_blank";
        a.download = filename || "audio";
        a.click();
        a.remove();
    };

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    };

    const menuPaperProps = {
        style: { backgroundColor: COLOR_BG, borderRadius: 8, minWidth: 72 },
    };

    const updateDuration = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (audio.duration !== Infinity && !Number.isNaN(audio.duration)) {
            setDuration(audio.duration);
        }
    };

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        updateDuration();
        audio.addEventListener("loadedmetadata", updateDuration);
        audio.addEventListener("durationchange", updateDuration);
        audio.addEventListener("durationchange", updateDuration);
        audio.addEventListener("canplaythrough", updateDuration);
        audio.addEventListener("progress", updateDuration);
    }, [src, audioRef.current]);

    return (
        <div
            className={`d-flex gap-8 ${isMobileScreen ? "flex-column" : "flex-row"}`}
            style={{
                backgroundColor: COLOR_BG,
                border: `1px solid ${COLOR_SURFACE}`,
                borderRadius: 12,
                padding: "6px 12px",
                boxSizing: "border-box",
                maxWidth: 400,
            }}
        >
            <audio ref={audioRef} src={src} onEnded={() => setIsPlaying(false)} onCanPlay={() => updateDuration()} onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime)}>
                <track kind="captions" />
            </audio>

            <div className="d-flex align-items-center gap-8 flex-grow-1">
                <IconButton onClick={togglePlay} size="small" className="color-main">
                    {isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
                </IconButton>

                <span className="tt-12 color-dark-6 text-align-right" style={{ whiteSpace: "nowrap", width: 35 }}>
                    {formatTime(currentTime)}
                </span>

                <Slider value={currentTime} max={duration || 0} onChange={handleSeek} className="color-main" style={{ padding: "10px 0", minWidth: 50 }} />

                <span className="tt-12 color-dark-6" style={{ whiteSpace: "nowrap", width: 35 }}>
                    {formatTime(duration)}
                </span>
            </div>

            {!hideDownload || !hideSpeed || !hideVolume ? (
                <div className={`d-flex align-items-center gap-8 ${isMobileScreen ? "justify-content-end" : ""}`}>
                    {!hideSpeed ? (
                        <>
                            <button
                                type="button"
                                className={`tt-12 cursor-pointer ${speed !== 1 ? "color-main" : "color-dark-6"}`}
                                style={{
                                    background: "none",
                                    border: `1px solid ${COLOR_SURFACE}`,
                                    borderRadius: 6,
                                    padding: "3px 7px",
                                    whiteSpace: "nowrap",
                                }}
                                onClick={(e) => setSpeedAnchor(e.currentTarget)}
                            >
                                {speed}x
                            </button>

                            <Menu anchorEl={speedAnchor} open={Boolean(speedAnchor)} onClose={() => setSpeedAnchor(null)} PaperProps={menuPaperProps} getContentAnchorEl={null} anchorOrigin={{ vertical: "top", horizontal: "center" }} transformOrigin={{ vertical: "bottom", horizontal: "center" }}>
                                {SPEEDS.map((s) => (
                                    <MenuItem
                                        className={`tt-12 justify-content-center ${s === speed ? "color-main" : "color-dark-6"}`}
                                        key={s}
                                        onClick={() => handleSpeed(s)}
                                        style={{
                                            fontWeight: s === speed ? 700 : 400,
                                            minHeight: 36,
                                        }}
                                    >
                                        {s}x
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    ) : null}

                    {!hideVolume ? (
                        <>
                            <IconButton onClick={(e) => setVolumeAnchor(volumeAnchor ? null : e.currentTarget)} size="small" className="color-dark-6">
                                {isMuted || volume === 0 ? <VolumeOff fontSize="small" className="color-main" /> : <VolumeUp fontSize="small" />}
                            </IconButton>

                            <Popover
                                open={Boolean(volumeAnchor)}
                                anchorEl={volumeAnchor}
                                onClose={() => setVolumeAnchor(null)}
                                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                                transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                                PaperProps={{
                                    style: {
                                        backgroundColor: COLOR_BG,
                                        borderRadius: 8,
                                        padding: "12px 8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        paddingBlock: 16,
                                        overflow: "hidden",
                                    },
                                }}
                            >
                                <div className="d-flex flex-column gap-16 align-items-center" style={{ minWidth: 32 }}>
                                    <Slider value={isMuted ? 0 : volume} min={0} max={1} step={0.05} onChange={handleVolume} orientation="vertical" className="color-main" style={{ height: 64 }} />

                                    <span className="tt-12 color-dark-6">{((volume ?? 0) * 100).toFixed(0)}%</span>
                                </div>
                            </Popover>
                        </>
                    ) : null}

                    {!hideDownload ? (
                        <IconButton onClick={handleDownload} size="small" className="color-dark-6">
                            <GetApp fontSize="small" />
                        </IconButton>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

export default CustomAudio;
